/**
 * What a wiki link is allowed to DO, handed down from whoever mounted the editor.
 *
 * A broken link's click offers to create the page (`070` §7.2) — but *where* that page goes
 * is a notebook decision, not an editor one: `070` §7.6 says a page created from a link on
 * `Ithkar` becomes a **child of `Ithkar`**, "since that is almost always the right home for
 * `[[The Grey Vault]]` written on `Ithkar`". The editor does not know about parents,
 * positions or fractional indices, and it must not learn: `111` §2 forbids a `features/*`
 * folder from importing another, and this context is how that rule is kept without the
 * editor growing a dependency on the page tree.
 *
 * `PageView` provides it; the wiki-link node view consumes it. Node views are portaled into
 * the editor's React tree (`ReactRenderer` → `editor.contentComponent`), so React context
 * reaches them normally.
 */
import { createContext, use } from 'react';

export interface WikiLinkActions {
  /** Create the page this broken link points at, and let §7.4 resolve the graph. */
  createPage: (label: string) => void;
}

const WikiLinkContext = createContext<WikiLinkActions | null>(null);

export const WikiLinkProvider = WikiLinkContext.Provider;

/**
 * `null` outside a provider, deliberately: the node view then renders the broken link as a
 * plain, non-interactive marker rather than throwing. An editor mounted somewhere that
 * cannot create pages (a preview, a test) is not a bug.
 */
export function useWikiLinkActions(): WikiLinkActions | null {
  return use(WikiLinkContext);
}
